// =============================================================================
// Theme Context
// =============================================================================

import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';

// Theme types
const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system'
};

// Initial state
const initialState = {
  theme: THEMES.SYSTEM,
  resolvedTheme: THEMES.LIGHT, // The actual theme being used
  systemTheme: THEMES.LIGHT,
  isLoading: false
};

// Action types
const THEME_ACTIONS = {
  SET_THEME: 'SET_THEME',
  SET_RESOLVED_THEME: 'SET_RESOLVED_THEME',
  SET_SYSTEM_THEME: 'SET_SYSTEM_THEME',
  SET_LOADING: 'SET_LOADING'
};

// Reducer
function themeReducer(state, action) {
  switch (action.type) {
    case THEME_ACTIONS.SET_THEME:
      return {
        ...state,
        theme: action.payload
      };
      
    case THEME_ACTIONS.SET_RESOLVED_THEME:
      return {
        ...state,
        resolvedTheme: action.payload
      };
      
    case THEME_ACTIONS.SET_SYSTEM_THEME:
      return {
        ...state,
        systemTheme: action.payload
      };
      
    case THEME_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
      
    default:
      return state;
  }
}

// Create context
const ThemeContext = createContext(null);

// Provider component
export function ThemeProvider({ children }) {
  const [state, dispatch] = useReducer(themeReducer, initialState);

  // Get system theme preference
  const getSystemTheme = useCallback(() => {
    if (typeof window === 'undefined') return THEMES.LIGHT;
    
    return window.matchMedia('(prefers-color-scheme: dark)').matches 
      ? THEMES.DARK 
      : THEMES.LIGHT;
  }, []);

  // Resolve the actual theme to use
  const resolveTheme = useCallback((theme, systemTheme) => {
    switch (theme) {
      case THEMES.LIGHT:
        return THEMES.LIGHT;
      case THEMES.DARK:
        return THEMES.DARK;
      case THEMES.SYSTEM:
        return systemTheme;
      default:
        return THEMES.LIGHT;
    }
  }, []);

  // Initialize theme
  const initialize = useCallback(() => {
    try {
      // Get saved theme preference
      const savedTheme = localStorage.getItem('theme') || THEMES.SYSTEM;
      
      // Get system theme
      const systemTheme = getSystemTheme();
      
      // Resolve actual theme
      const resolvedTheme = resolveTheme(savedTheme, systemTheme);
      
      dispatch({ type: THEME_ACTIONS.SET_THEME, payload: savedTheme });
      dispatch({ type: THEME_ACTIONS.SET_SYSTEM_THEME, payload: systemTheme });
      dispatch({ type: THEME_ACTIONS.SET_RESOLVED_THEME, payload: resolvedTheme });
    } catch (error) {
      console.error('Failed to initialize theme:', error);
    }
  }, [getSystemTheme, resolveTheme]);

  // Set theme
  const setTheme = useCallback((newTheme) => {
    try {
      // Validate theme
      if (!Object.values(THEMES).includes(newTheme)) {
        throw new Error(`Invalid theme: ${newTheme}`);
      }
      
      // Save to localStorage
      localStorage.setItem('theme', newTheme);
      
      // Update state
      dispatch({ type: THEME_ACTIONS.SET_THEME, payload: newTheme });
      
      // Resolve and update actual theme
      const resolvedTheme = resolveTheme(newTheme, state.systemTheme);
      dispatch({ type: THEME_ACTIONS.SET_RESOLVED_THEME, payload: resolvedTheme });
      
    } catch (error) {
      console.error('Failed to set theme:', error);
    }
  }, [resolveTheme, state.systemTheme]);

  // Toggle between light and dark
  const toggleTheme = useCallback(() => {
    const currentResolved = state.resolvedTheme;
    const newTheme = currentResolved === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK;
    setTheme(newTheme);
  }, [state.resolvedTheme, setTheme]);

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      const newSystemTheme = e.matches ? THEMES.DARK : THEMES.LIGHT;
      dispatch({ type: THEME_ACTIONS.SET_SYSTEM_THEME, payload: newSystemTheme });
      
      // If using system theme, update resolved theme
      if (state.theme === THEMES.SYSTEM) {
        dispatch({ type: THEME_ACTIONS.SET_RESOLVED_THEME, payload: newSystemTheme });
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [state.theme]);

  // Apply theme to document
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    
    // Remove all theme classes
    root.classList.remove('light', 'dark');
    
    // Add current theme class
    root.classList.add(state.resolvedTheme);
    
    // Update meta theme-color
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        'content', 
        state.resolvedTheme === THEMES.DARK ? '#1a1a1a' : '#ffffff'
      );
    }
  }, [state.resolvedTheme]);

  // Get theme-specific values
  const getThemeValue = useCallback((lightValue, darkValue) => {
    return state.resolvedTheme === THEMES.DARK ? darkValue : lightValue;
  }, [state.resolvedTheme]);

  // Check if current theme is dark
  const isDark = state.resolvedTheme === THEMES.DARK;
  
  // Check if current theme is light
  const isLight = state.resolvedTheme === THEMES.LIGHT;
  
  // Check if using system theme
  const isSystem = state.theme === THEMES.SYSTEM;

  const value = {
    // State
    theme: state.theme,
    resolvedTheme: state.resolvedTheme,
    systemTheme: state.systemTheme,
    isLoading: state.isLoading,
    
    // Computed
    isDark,
    isLight,
    isSystem,
    
    // Actions
    initialize,
    setTheme,
    toggleTheme,
    getThemeValue,
    
    // Constants
    themes: THEMES
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// Hook to use theme context
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
